import React, { useMemo, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import TableChartIcon from '@mui/icons-material/TableChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { MappingResponse } from '../../services/api';
import type { PipelineTargetTable } from './types';
import { SOURCE_COLOR, TARGET_COLOR } from './types';
import { buildPipelineModel } from './buildPipelineModel';
import PipelineNodeDetails from './PipelineNodeDetails';
import PipelineBottomPanel from './PipelineBottomPanel';

/* ─── Constants ─────────────────────────────────────────── */
const SRC_COL_X = 40;          // x offset for source column
const TGT_COL_X = 620;         // x offset for target column
const ROW_HEIGHT = 52;          // vertical spacing per row
const NODE_W = 220;             // node card width
const NODE_H = 42;              // node card height
const TOP_PAD = 24;             // top padding

/* ─── Color palette for edges (cycle through) ───────────── */
const EDGE_PALETTE = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#22c55e', '#06b6d4', '#ef4444',
];

interface PipelineViewProps {
  mappingResponse?: MappingResponse | null;
  sqlByTarget?: Record<string, string>;
  onDrillIntoTable?: (tableId: string, role: 'source' | 'target') => void;
  /** Externally-controlled target selection (from Navigator sidebar) */
  selectedTarget?: string | null;
  onSelectTarget?: (name: string | null) => void;
  /** Multi-select: sets of selected source/target table ids from sidebar */
  selectedTargetIds?: Set<string>;
  selectedSourceIds?: Set<string>;
}

/* ─── Small Source / Target Node ────────────────────────── */
const SourceNode: React.FC<{
  alias: string;
  x: number;
  y: number;
  color: string;
  highlighted: boolean;
  onHover: (alias: string | null) => void;
  onDrillIn?: () => void;
}> = ({ alias, x, y, color, highlighted, onHover, onDrillIn }) => (
  <foreignObject x={x} y={y} width={NODE_W} height={NODE_H}>
    <Box
      onMouseEnter={() => onHover(alias)}
      onMouseLeave={() => onHover(null)}
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        bgcolor: highlighted ? `${color}14` : '#fff',
        border: `1.5px solid ${highlighted ? color : '#e2e8f0'}`,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'all 0.15s',
        cursor: 'default',
      }}
    >
      <StorageIcon sx={{ fontSize: 14, color, flexShrink: 0 }} />
      <Typography variant="body2" fontWeight={600} sx={{ fontSize: 11, color: '#334155', flex: 1 }} noWrap>
        {alias}
      </Typography>
      {onDrillIn && (
        <Tooltip title="View field details" arrow>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDrillIn(); }}
            sx={{ p: 0.25, color: '#94a3b8', '&:hover': { color: SOURCE_COLOR } }}
          >
            <VisibilityIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </foreignObject>
);

const TargetNode: React.FC<{
  target: PipelineTargetTable;
  x: number;
  y: number;
  selected: boolean;
  highlighted: boolean;
  onClick: () => void;
  onHover: (name: string | null) => void;
  onDrillIn?: () => void;
}> = ({ target, x, y, selected, highlighted, onClick, onHover, onDrillIn }) => {
  const confPct = Math.round(target.avgConfidence * 100);
  const confColor = confPct >= 90 ? '#22c55e' : confPct >= 70 ? '#f59e0b' : '#ef4444';

  return (
    <foreignObject x={x} y={y} width={NODE_W + 60} height={NODE_H}>
      <Box
        onClick={onClick}
        onMouseEnter={() => onHover(target.name)}
        onMouseLeave={() => onHover(null)}
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          bgcolor: selected ? `${TARGET_COLOR}10` : highlighted ? '#f0fdf4' : '#fff',
          border: `1.5px solid ${selected ? TARGET_COLOR : highlighted ? `${TARGET_COLOR}80` : '#e2e8f0'}`,
          borderRadius: '8px',
          boxShadow: selected
            ? `0 0 0 2px ${TARGET_COLOR}30, 0 1px 4px rgba(0,0,0,0.08)`
            : '0 1px 3px rgba(0,0,0,0.06)',
          transition: 'all 0.15s',
          cursor: 'pointer',
          '&:hover': { borderColor: TARGET_COLOR },
        }}
      >
        <TableChartIcon sx={{ fontSize: 14, color: TARGET_COLOR, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: 11, color: '#1e293b', flex: 1 }} noWrap>
          {target.name}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
          {target.stepCount}f
        </Typography>
        <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10, color: confColor, flexShrink: 0 }}>
          {confPct}%
        </Typography>
        {onDrillIn && (
          <Tooltip title="View field details" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDrillIn(); }}
              sx={{ p: 0.25, ml: 0.25, color: '#94a3b8', '&:hover': { color: TARGET_COLOR } }}
            >
              <VisibilityIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </foreignObject>
  );
};

/* ─── Main View ─────────────────────────────────────────── */
const PipelineView: React.FC<PipelineViewProps> = ({
  mappingResponse,
  sqlByTarget,
  onDrillIntoTable,
  selectedTarget: externalSelectedTarget,
  onSelectTarget,
  selectedTargetIds,
  selectedSourceIds,
}) => {
  const [internalSelectedTarget, setInternalSelectedTarget] = useState<string | null>(null);
  const selectedTargetName = externalSelectedTarget !== undefined ? externalSelectedTarget : internalSelectedTarget;
  const setSelectedTargetName = onSelectTarget ?? setInternalSelectedTarget;
  const [hoveredSource, setHoveredSource] = useState<string | null>(null);
  const [hoveredTarget, setHoveredTarget] = useState<string | null>(null);

  // Determine if sidebar multi-select filtering is active
  const hasTableFilter = (selectedTargetIds?.size ?? 0) + (selectedSourceIds?.size ?? 0) > 0;

  const model = useMemo(
    () => buildPipelineModel(mappingResponse),
    [mappingResponse],
  );

  const selectedTarget: PipelineTargetTable | null = useMemo(
    () => model.targets.find((t) => t.name === selectedTargetName) ?? null,
    [model.targets, selectedTargetName],
  );

  // Assign stable colors to each source
  const sourceColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    model.sources.forEach((s, i) => {
      map[s.alias] = EDGE_PALETTE[i % EDGE_PALETTE.length];
    });
    return map;
  }, [model.sources]);

  // Compute positions
  const srcCount = model.sources.length;
  const tgtCount = model.targets.length;
  const svgHeight = Math.max(srcCount, tgtCount) * ROW_HEIGHT + TOP_PAD * 2;
  const svgWidth = TGT_COL_X + NODE_W + 80;

  // Source Y positions (centered vertically)
  const srcStartY = TOP_PAD + Math.max(0, (tgtCount - srcCount) * ROW_HEIGHT / 2);
  const tgtStartY = TOP_PAD + Math.max(0, (srcCount - tgtCount) * ROW_HEIGHT / 2);

  // Determine which edges to highlight
  const highlightedEdges = useMemo(() => {
    // Sidebar multi-select filter takes priority
    if (hasTableFilter) {
      return new Set(
        model.edges
          .filter((e) => {
            const srcMatch = !selectedSourceIds?.size || selectedSourceIds.has(e.from);
            const tgtMatch = !selectedTargetIds?.size || selectedTargetIds.has(e.to);
            return srcMatch && tgtMatch;
          })
          .map((e) => `${e.from}→${e.to}`),
      );
    }
    if (selectedTargetName) {
      return new Set(
        model.edges
          .filter((e) => e.to === selectedTargetName)
          .map((e) => `${e.from}→${e.to}`),
      );
    }
    if (hoveredSource) {
      return new Set(
        model.edges
          .filter((e) => e.from === hoveredSource)
          .map((e) => `${e.from}→${e.to}`),
      );
    }
    if (hoveredTarget) {
      return new Set(
        model.edges
          .filter((e) => e.to === hoveredTarget)
          .map((e) => `${e.from}→${e.to}`),
      );
    }
    return null; // null = show all
  }, [selectedTargetName, hoveredSource, hoveredTarget, model.edges, hasTableFilter, selectedSourceIds, selectedTargetIds]);

  if (!mappingResponse?.transformation_plan) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 1,
          height: '100%',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No Transformation Pipeline
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate mappings first to view the transformation pipeline.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack direction="row" sx={{ flex: 1, height: '100%', overflow: 'hidden' }}>
      {/* Center: Pipeline graph + Bottom Panel */}
      <Stack sx={{ flex: 1, overflow: 'hidden' }}>
        {/* SVG Lineage Graph */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            bgcolor: '#f8fafc',
            position: 'relative',
          }}
        >
          <svg
            width={svgWidth}
            height={svgHeight}
            style={{ display: 'block', minWidth: '100%' }}
          >
            {/* Column headers + summary */}
            <text x={SRC_COL_X} y={16} fontSize="11" fontWeight="700" fill="#64748b" fontFamily="sans-serif">
              SOURCES ({model.sources.length})
            </text>
            <text x={TGT_COL_X} y={16} fontSize="11" fontWeight="700" fill="#64748b" fontFamily="sans-serif">
              TARGETS ({model.targets.length})
            </text>
            <text x={svgWidth - 8} y={16} fontSize="10" fill="#94a3b8" fontFamily="sans-serif" textAnchor="end">
              {model.edges.length} connections
            </text>

            {/* Edge lines (draw first so nodes are on top) */}
            {model.edges.map((edge) => {
              const srcIdx = model.sources.findIndex((s) => s.alias === edge.from);
              const tgtIdx = model.targets.findIndex((t) => t.name === edge.to);
              if (srcIdx < 0 || tgtIdx < 0) return null;

              const srcY = srcStartY + srcIdx * ROW_HEIGHT + NODE_H / 2;
              const tgtY = tgtStartY + tgtIdx * ROW_HEIGHT + NODE_H / 2;
              const x1 = SRC_COL_X + NODE_W + 4;
              const x2 = TGT_COL_X - 4;
              const cx1 = x1 + (x2 - x1) * 0.45;
              const cx2 = x1 + (x2 - x1) * 0.55;

              const edgeKey = `${edge.from}→${edge.to}`;
              const isHighlighted = highlightedEdges === null || highlightedEdges.has(edgeKey);
              const color = sourceColorMap[edge.from] || '#94a3b8';

              return (
                <path
                  key={edgeKey}
                  d={`M${x1},${srcY} C${cx1},${srcY} ${cx2},${tgtY} ${x2},${tgtY}`}
                  stroke={color}
                  strokeWidth={isHighlighted ? 2 : 1}
                  strokeOpacity={isHighlighted ? 0.7 : 0.12}
                  fill="none"
                  style={{ transition: 'stroke-opacity 0.2s, stroke-width 0.2s' }}
                />
              );
            })}

            {/* Source nodes */}
            {model.sources.map((src, i) => {
              const y = srcStartY + i * ROW_HEIGHT;
              const isSidebarSelected = selectedSourceIds?.has(src.alias) ?? false;
              const isHighlighted =
                isSidebarSelected ||
                hoveredSource === src.alias ||
                (selectedTargetName != null &&
                  model.edges.some(
                    (e) => e.from === src.alias && e.to === selectedTargetName,
                  ));
              const isDimmed = hasTableFilter && !isSidebarSelected &&
                !(selectedTargetIds?.size && model.edges.some((e) => e.from === src.alias && selectedTargetIds.has(e.to)));
              return (
                <g key={src.alias} style={{ opacity: isDimmed ? 0.35 : 1, transition: 'opacity 0.2s' }}>
                  <SourceNode
                    alias={src.alias}
                    x={SRC_COL_X}
                    y={y}
                    color={sourceColorMap[src.alias] || SOURCE_COLOR}
                    highlighted={isHighlighted}
                    onHover={setHoveredSource}
                    onDrillIn={onDrillIntoTable ? () => onDrillIntoTable(src.alias, 'source') : undefined}
                  />
                </g>
              );
            })}

            {/* Target nodes */}
            {model.targets.map((tgt, i) => {
              const y = tgtStartY + i * ROW_HEIGHT;
              const isSidebarSelected = selectedTargetIds?.has(tgt.name) ?? false;
              const isDimmed = hasTableFilter && !isSidebarSelected &&
                !(selectedSourceIds?.size && model.edges.some((e) => e.to === tgt.name && selectedSourceIds.has(e.from)));
              return (
                <g key={tgt.name} style={{ opacity: isDimmed ? 0.35 : 1, transition: 'opacity 0.2s' }}>
                  <TargetNode
                    target={tgt}
                    x={TGT_COL_X}
                    y={y}
                    selected={isSidebarSelected || selectedTargetName === tgt.name}
                    highlighted={
                      hoveredTarget === tgt.name ||
                      (hoveredSource != null &&
                        model.edges.some(
                          (e) => e.from === hoveredSource && e.to === tgt.name,
                        ))
                    }
                    onClick={() =>
                      setSelectedTargetName(
                        selectedTargetName === tgt.name ? null : tgt.name,
                      )
                    }
                    onHover={setHoveredTarget}
                    onDrillIn={onDrillIntoTable ? () => onDrillIntoTable(tgt.name, 'target') : undefined}
                  />
                </g>
              );
            })}
          </svg>
        </Box>

        {/* Bottom Panel: Tabs (Chain + Code + Summary) */}
        <PipelineBottomPanel
          sqlByTarget={sqlByTarget}
          selectedTarget={selectedTarget}
          joins={model.joins}
        />
      </Stack>

      {/* Right: Target Details (field mappings) */}
      {selectedTarget && (
        <PipelineNodeDetails
          target={selectedTarget}
          onClose={() => setSelectedTargetName(null)}
        />
      )}
    </Stack>
  );
};

export default PipelineView;
